/**
 * event-buffer.ts — Ring buffer and subscription delivery engine.
 *
 * Buffers events in a fixed-size ring buffer and delivers matching
 * events to subscribed napplets via the abstract sendToNapplet transport.
 */

import type { NostrEvent, NostrFilter } from '@napplet/core';
import type { Capability } from '@kehto/acl/capabilities';
import type { SendToNapplet } from './types.js';
import type { SessionRegistry } from './session-registry.js';
import type { EnforceResult } from './enforce.js';

/** Default ring buffer size. */
export const RING_BUFFER_SIZE = 100;

/** Subscription entry — tracks a subscription for a specific napplet window. */
export interface SubscriptionEntry {
  windowId: string;
  filters: NostrFilter[];
}

/**
 * Check if an event matches a single NIP-01 filter.
 * Pure function — no side effects.
 *
 * @param event - The event to check
 * @param filter - The filter to match against
 * @returns True if the event matches the filter
 *
 * @example
 * ```ts
 * import { matchesFilter } from '@kehto/runtime';
 *
 * matchesFilter(event, { kinds: [1], authors: ['abc'] });
 * // true if event.kind === 1 and event.pubkey starts with 'abc'
 * ```
 */
export function matchesFilter(event: NostrEvent, filter: NostrFilter): boolean {
  if (filter.ids !== undefined && !filter.ids.some((id) => event.id.startsWith(id))) return false;
  if (filter.authors !== undefined && !filter.authors.some((a) => event.pubkey.startsWith(a))) return false;
  if (filter.kinds !== undefined && !filter.kinds.includes(event.kind)) return false;
  if (filter.since !== undefined && event.created_at < filter.since) return false;
  if (filter.until !== undefined && event.created_at > filter.until) return false;
  for (const [key, values] of Object.entries(filter)) {
    if (!key.startsWith('#') || values === undefined) continue;
    const tagName = key.slice(1);
    const tagValues = values as string[];
    const eventTagValues = event.tags.filter((t) => t[0] === tagName).map((t) => t[1]);
    if (!tagValues.some((v) => eventTagValues.includes(v))) return false;
  }
  return true;
}

/**
 * Check if an event matches any filter in a list.
 * Returns true for empty filter lists.
 *
 * @param event - The event to check
 * @param filters - The filters to match against
 * @returns True if the event matches any filter (or if filters is empty)
 *
 * @example
 * ```ts
 * import { matchesAnyFilter } from '@kehto/runtime';
 *
 * matchesAnyFilter(event, [{ kinds: [1] }, { kinds: [7] }]);
 * // true if event is kind 1 or kind 7
 * ```
 */
export function matchesAnyFilter(event: NostrEvent, filters: NostrFilter[]): boolean {
  if (filters.length === 0) return true;
  return filters.some((filter) => matchesFilter(event, filter));
}

/** Event buffer and subscription delivery engine. */
export interface EventBuffer {
  /** Add an event to the ring buffer and deliver to matching subscriptions. */
  bufferAndDeliver(event: NostrEvent, senderId: string | null): void;

  /** Deliver an event to matching subscriptions without buffering. */
  deliverToSubscriptions(event: NostrEvent, senderId: string | null): void;

  /** Get the current subscription map (for REQ handler to register subs). */
  getSubscriptions(): Map<string, SubscriptionEntry>;

  /** Get buffered events (for REQ replay). */
  getBufferedEvents(): readonly NostrEvent[];

  /** Clear the buffer. */
  clear(): void;
}

/**
 * Create an event buffer with subscription delivery.
 *
 * @param sendToNapplet - Transport function to send messages to napplets
 * @param sessionRegistry - Identity registry for looking up napplet pubkeys
 * @param enforce - Enforcement function for checking relay:read on recipients
 * @param subscriptions - Shared subscription map (owned by the runtime)
 * @param getBufferSize - Optional getter for a dynamic buffer size override.
 *   When provided, its return value is used instead of RING_BUFFER_SIZE.
 *   Called on every bufferAndDeliver, so changes take effect immediately.
 * @returns An EventBuffer instance
 *
 * @example
 * ```ts
 * import { createEventBuffer } from '@kehto/runtime';
 *
 * const buffer = createEventBuffer(sendToNapplet, sessionRegistry, enforce, subscriptions);
 * buffer.bufferAndDeliver(event, senderWindowId);
 * ```
 */
export function createEventBuffer(
  sendToNapplet: SendToNapplet,
  sessionRegistry: SessionRegistry,
  enforce: (pubkey: string, capability: Capability, message?: unknown[]) => EnforceResult,
  subscriptions: Map<string, SubscriptionEntry>,
  getBufferSize?: () => number,
): EventBuffer {
  const buffer: NostrEvent[] = [];

  function deliverToSubscriptions(event: NostrEvent, senderId: string | null): void {
    const pTag = event.tags?.find((t) => t[0] === 'p');
    const targetPubkey = pTag?.[1];

    for (const [subKey, sub] of subscriptions) {
      if (senderId !== null && sub.windowId === senderId) continue;

      const recipientPubkey = sessionRegistry.getPubkey(sub.windowId);
      if (recipientPubkey) {
        const recipientResult = enforce(recipientPubkey, 'relay:read', ['EVENT', event]);
        if (!recipientResult.allowed) continue;
      }

      if (targetPubkey) {
        const subPubkey = recipientPubkey;
        if (subPubkey !== targetPubkey) continue;
      }

      if (!matchesAnyFilter(event, sub.filters)) continue;

      const prefix = `${sub.windowId}:`;
      if (!subKey.startsWith(prefix)) continue;
      const subId = subKey.slice(prefix.length);

      sendToNapplet(sub.windowId, ['EVENT', subId, event]);
    }
  }

  return {
    bufferAndDeliver(event: NostrEvent, senderId: string | null): void {
      const maxSize = getBufferSize?.() ?? RING_BUFFER_SIZE;
      if (buffer.length >= maxSize) buffer.shift();
      buffer.push(event);
      deliverToSubscriptions(event, senderId);
    },

    deliverToSubscriptions,

    getSubscriptions(): Map<string, SubscriptionEntry> { return subscriptions; },

    getBufferedEvents(): readonly NostrEvent[] { return buffer; },

    clear(): void {
      buffer.length = 0;
    },
  };
}
